import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { concatMap, fromEvent, map, mergeMap, Observable, of, take, tap, toArray } from 'rxjs';
import { appSettings } from '../../../environments/app-settings';
import { disableErrorHandler } from '../../http/error.interceptor';

/**
 * Upload URL(s) response body payload.
 */
export interface UploadUrlResponse {
  /**
   * File ID.
   */
  id: string;
  /**
   * Single request file upload URL.
   */
  uploadUrl: string;
  /**
   * File block (chunk) upload URL.
   * Use together with with commitUrl.
   */
  uploadBlockUrl: string;
  /**
   * Commit (complete) file upload url.
   * Use together with with uploadBlockUrl.
   */
  commitUrl: string;
  /**
   * Delete file upload (invalidate upload URL) URL.
   */
  deleteUrl: string;
  /**
   * UTC date time of upload URL expiration.
   */
  expiresOn: Date;
}

interface SliceInfo {
  startByte: number;
  endByte: number;
  id: number;
}

interface BatchInfo {
  slice: SliceInfo;
  batch: string | ArrayBuffer | null;
}

@Injectable()
export class FileUploadService {
  // fields
  private readonly http = inject(HttpClient);

  private readonly baseUrl = appSettings.fus.url;
  private readonly X_MS_META_ = 'x-ms-meta-';
  private readonly BATCH_SIZE = 5 * 1024 * 1024; // 5 MB
  private readonly X_SYK_APPLICATION_ID = 'x-syk-application-id';
  private readonly uploadedFor = appSettings.fus.uploadedFor;

  // upload methods
  public getUploadUrl(fileName: string, uploadID: string, countryCode: string, unpack: boolean): Observable<UploadUrlResponse> {
    const url = `${this.baseUrl}/api/upload/url`;

    let params = new HttpParams();
    if (fileName)
      params = params.set('fileName', fileName);
    if (uploadID)
      params = params.set('uploadID', uploadID);
    if (countryCode)
      params = params.set('countryCode', countryCode);
    if (unpack)
      params = params.set('unpack', 'true');

    const headers = new HttpHeaders()
      .set(this.X_SYK_APPLICATION_ID, this.uploadedFor);

    return this.http.get<UploadUrlResponse>(url, { headers, params, context: disableErrorHandler() });
  }

  public upload(urlResponse: UploadUrlResponse, file: File, metadata: object,
    reportStatus: (message: string) => void = _ => { /* defaults to do nothing */ },
    reportProgress: (progress: number) => void = _ => { /* defaults to do nothing */ }): Observable<any> {
    return file.size > this.BATCH_SIZE
      ? this.sliceFile(file, urlResponse, metadata, reportStatus, reportProgress)
      : this.uploadFile(file, urlResponse, metadata, reportStatus, reportProgress);
  }

  private uploadFile(file: File, urlResponse: UploadUrlResponse, metadata: object, reportStatus: (message: string) => void, reportProgress: (progress: number) => void): Observable<any> {
    reportStatus(`${new Date().toLocaleString()} upload to '${urlResponse.uploadUrl.split('?')[0]}'`);
    reportProgress(0);
    reportStatus(`${new Date().toLocaleString()} starting single request upload blob id = '${urlResponse.id}' size = '${(file.size / 1024 / 1024).toFixed(2)} MB'`);

    const headers = this.createHeaders(metadata);
    return this.http.put(urlResponse.uploadUrl, file, { headers, context: disableErrorHandler() }).pipe(
      tap(() => {
        reportProgress(100);
        reportStatus(`${new Date().toLocaleString()} single request upload completed successfully`);
      })
    );
  }

  private sliceFile(file: File, urlResponse: UploadUrlResponse, metadata: object, reportStatus: (message: string) => void, reportProgress: (progress: number) => void): Observable<any> {
    reportStatus(`${new Date().toLocaleString()} upload to '${urlResponse.uploadBlockUrl.split('?')[0]}'`);

    const fileSize = file.size;

    reportProgress(0);
    reportStatus(`${new Date().toLocaleString()} starting batch processing blob id = '${urlResponse.id}' size = '${(fileSize / 1024 / 1024).toFixed(2)} MB' BATCH = '${this.BATCH_SIZE / 1024 / 1024} MB'`);

    let bytesSend = 0;
    const slices = this.getSlices(fileSize);

    reportStatus(`${new Date().toLocaleString()} total batches ${slices.length}`);

    return of(...slices).pipe(
      concatMap(slice => this.readAndSendSlice(file, slice, urlResponse.uploadBlockUrl)),
      tap(slice => {
        bytesSend += slice.endByte - slice.startByte;
        reportProgress(Math.round(bytesSend * 100 / fileSize));
      }),
      toArray(),
      map(blocks => {
        reportStatus(`${new Date().toLocaleString()} start commit`);
        return blocks.sort((a, b) => a.id - b.id);
      }),
      mergeMap(blocks => this.commitBlob(urlResponse.commitUrl, blocks, metadata)),
      tap(() => {
        reportProgress(100);
        reportStatus(`${new Date().toLocaleString()} blob committed successfully`);
      })
    );
  }

  private readAndSendSlice(file: File, slice: SliceInfo, url: string): Observable<SliceInfo> {
    return this.readSlice(file, slice).pipe(
      mergeMap(batch => this.sendBatch(url, batch).pipe(
        map(_ => slice)
      ))
    );
  }

  private sendBatch(url: string, batch: BatchInfo): Observable<any> {
    const params = new HttpParams()
      .set('blockid', batch.slice.id);

    return this.http.put(url, batch.batch, { params, context: disableErrorHandler() });
  }

  private commitBlob(url: string, slices: SliceInfo[], metadata: object): Observable<any> {
    const payload = slices.map(s => s.id);
    const headers = this.createHeaders(metadata);
    return this.http.put(url, payload, { headers, context: disableErrorHandler() });
  }

  // delete/restore methods
  public delete(urlResponse: UploadUrlResponse): Observable<any> {
    return this.http.delete<any>(urlResponse.deleteUrl);
  }

  public deleteMetadata(fileID: string): Observable<any> {
    const url = `${this.baseUrl}/api/metadata/${fileID}`;
    return this.http.delete<any>(url);
  }

  public restore(fileID: string): Observable<any> {
    const url = `${this.baseUrl}/api/restore/${fileID}`;
    return this.http.post<any>(url, null);
  }

  // helper methods
  private getSlices(fileSize: number): SliceInfo[] {
    const slices = [];

    let startByte = 0;
    let endByte: number;
    let complete = false;
    let id = 0;

    while (!complete) {
      // case 1: file is less than batch size -> send file as one batch
      if (this.BATCH_SIZE >= fileSize) {
        endByte = fileSize;
        complete = true;
      } else {
        let bytesLeft = fileSize - startByte;
        // case 2: last batch
        if (this.BATCH_SIZE >= bytesLeft) {
          endByte = fileSize;
          complete = true;
        } else {
          // case 3: start batch or something in the middle of a file
          endByte = startByte + this.BATCH_SIZE;
          complete = false;
        }
      }

      const slice: SliceInfo = {
        startByte,
        endByte,
        id: ++id
      };

      slices.push(slice);

      if (!complete)
        startByte = endByte;
    }

    return slices;
  }

  private readSlice(file: File, slice: SliceInfo): Observable<BatchInfo> {
    const reader = new FileReader();

    const observable: Observable<BatchInfo> = fromEvent(reader, 'load').pipe(
      map(_ => { return { slice: slice, batch: reader.result }; }),
      take(1)
    );

    const blob = file.slice(slice.startByte, slice.endByte);
    reader.readAsArrayBuffer(blob);

    return observable;
  }

  private createHeaders(metadata: object): HttpHeaders {
    let headers = new HttpHeaders();

    for (let prop in metadata) {
      // @ts-ignore
      let value = metadata[prop];
      if (value)
        headers = headers.set(`${this.X_MS_META_}${prop}`, value);
    }

    return headers;
  }
}
